import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/gestionale/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/gestionale/integrations/supabase/types";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, Search, Handshake, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/gestionale/components/ui/button";
import { Input } from "@/gestionale/components/ui/input";
import { Badge } from "@/gestionale/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/gestionale/components/ui/table";
import { toast } from "sonner";
import SponsorDialog from "@/gestionale/components/sponsor/SponsorDialog";
import DeleteSponsorDialog from "@/gestionale/components/sponsor/DeleteSponsorDialog";

type Sponsor = Tables<"sponsors">;

const TIER_COLORS: Record<string, string> = {
  oro: "bg-yellow-500/15 text-yellow-700 border-yellow-500/30",
  argento: "bg-zinc-400/15 text-zinc-600 border-zinc-400/30",
  bronzo: "bg-orange-500/15 text-orange-700 border-orange-500/30",
};

export default function Sponsor() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
  const [deletingSponsor, setDeletingSponsor] = useState<Sponsor | null>(null);

  const { data: sponsors = [], isLoading } = useQuery({
    queryKey: ["sponsors-gestionale"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sponsors")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async (sponsor: TablesInsert<"sponsors"> & { id?: string }) => {
      if (sponsor.id) {
        const { id, ...rest } = sponsor;
        const { error } = await supabase.from("sponsors").update(rest).eq("id", id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("sponsors").insert(sponsor);
        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sponsors-gestionale"] });
      toast.success(variables.id ? "Sponsor aggiornato" : "Sponsor creato");
      setDialogOpen(false);
      setEditingSponsor(null);
    },
    onError: (error) => {
      toast.error("Errore: " + error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("sponsors").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sponsors-gestionale"] });
      toast.success("Sponsor eliminato");
      setDeletingSponsor(null);
    },
    onError: (error) => {
      toast.error("Errore: " + error.message);
    },
  });

  const filtered = sponsors.filter((s) => {
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q);
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sponsor</h1>
          <p className="text-muted-foreground mt-1">Gestione sponsor visibili sul sito pubblico</p>
        </div>
        <Button onClick={() => { setEditingSponsor(null); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" /> Nuovo Sponsor
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cerca sponsor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="p-4 rounded-2xl bg-primary/10 mb-4">
              <Handshake className="h-10 w-10 text-primary" />
            </div>
            <p className="text-lg font-semibold">Nessuno sponsor trovato</p>
            <p className="text-sm text-muted-foreground mt-1">
              {search ? "Prova a modificare la ricerca" : "Clicca \"Nuovo Sponsor\" per aggiungerne uno"}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Livello</TableHead>
                <TableHead className="hidden md:table-cell">Telefono</TableHead>
                <TableHead className="hidden md:table-cell">Sito</TableHead>
                <TableHead className="hidden md:table-cell">Scadenza</TableHead>
                <TableHead className="text-center">Stato</TableHead>
                <TableHead className="w-[100px]">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((sponsor) => (
                <TableRow key={sponsor.id}>
                  <TableCell className="font-medium flex items-center gap-2">
                    {sponsor.logo_url && (
                      <img src={sponsor.logo_url} alt={sponsor.name} className="h-6 w-6 object-contain rounded" />
                    )}
                    {sponsor.name}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={TIER_COLORS[sponsor.tier] || ""}>
                      {sponsor.tier}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{sponsor.telefono || "—"}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {sponsor.website_url ? (
                      <a
                        href={sponsor.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Sito <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {sponsor.data_scadenza ? new Date(sponsor.data_scadenza).toLocaleDateString("it-IT") : "—"}
                  </TableCell>
                  <TableCell className="text-center">
                    {sponsor.active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle2 className="h-4 w-4" /> Attivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-red-600">
                        <XCircle className="h-4 w-4" /> Disattivo
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setEditingSponsor(sponsor); setDialogOpen(true); }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeletingSponsor(sponsor)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <SponsorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        sponsor={editingSponsor}
        onSave={(sponsor) => upsertMutation.mutate(sponsor)}
        isSaving={upsertMutation.isPending}
      />

      <DeleteSponsorDialog
        sponsor={deletingSponsor}
        onOpenChange={(open) => { if (!open) setDeletingSponsor(null); }}
        onConfirm={() => { if (deletingSponsor) deleteMutation.mutate(deletingSponsor.id); }}
        isDeleting={deleteMutation.isPending}
      />
    </motion.div>
  );
}